export const CHANGE_TYPE_ADD = "add" as const;
export const CHANGE_TYPE_REMOVE = "remove" as const;

export type ChangeType = typeof CHANGE_TYPE_ADD | typeof CHANGE_TYPE_REMOVE;

export type StagedChange = {
  type: ChangeType;
  flagName: string;
  cxId: string;
};

export function stageChangeHelper(prev: StagedChange[], change: StagedChange): StagedChange[] {
  // If opposite change exists, remove it instead of adding new one
  const oppositeIdx = prev.findIndex(
    c => c.flagName === change.flagName && c.cxId === change.cxId && c.type !== change.type
  );
  if (oppositeIdx >= 0) {
    return prev.filter((_, i) => i !== oppositeIdx);
  }

  // Check for duplicate
  const exists = prev.some(
    c => c.flagName === change.flagName && c.cxId === change.cxId && c.type === change.type
  );
  if (exists) return prev;

  return [...prev, change];
}

export function formatChangeForDiff(change: StagedChange): string {
  const prefix = change.type === CHANGE_TYPE_ADD ? "+" : "-";
  return `${prefix} ${change.flagName}: ${change.cxId}`;
}

export function formatChangesForClipboard(changes: StagedChange[]): string {
  return changes.map(formatChangeForDiff).join("\n");
}

export type FeatureFlag = {
  enabled: boolean;
  values?: string[];
};

/**
 * Builds the final feature flags payload by applying staged changes to the current state
 */
export function buildFinalPayload(
  currentFlags: Record<string, FeatureFlag>,
  changes: StagedChange[]
): Record<string, FeatureFlag> {
  // Deep copy the current flags
  const result: Record<string, FeatureFlag> = {};
  for (const [flagName, flag] of Object.entries(currentFlags)) {
    result[flagName] = {
      enabled: flag.enabled,
      values: flag.values ? [...flag.values] : undefined,
    };
  }

  // Apply each change
  for (const change of changes) {
    const flag = result[change.flagName];
    if (!flag || !flag.values) continue;

    if (change.type === CHANGE_TYPE_ADD) {
      if (!flag.values.includes(change.cxId)) {
        flag.values.push(change.cxId);
      }
    } else {
      flag.values = flag.values.filter(id => id !== change.cxId);
    }
  }

  return result;
}
